import React, { useRef, useState } from 'react';
import MetaHeader from 'components/MetaHeader';
import Header from 'components/Header';
import styled from 'styled-components';
import { Row, Col } from 'components/Layout/RowColLayout';
import MenuItem from 'components/MenuItem';
import { breakpoints } from 'constant/style';
import { IconButton } from 'components/Button/IconButton';
import { TruckSmallIcon, NoteIcon } from 'assets/svg';
import { Tabs, Modal } from 'antd';
import MenuDetails from 'components/MenuDetails';
import { InView } from 'react-intersection-observer';
import { isClient } from 'utils/common';
import router from 'next/router';
import { getMicrositeMenus } from 'services';
import * as ga from 'lib/ga';
import { TRACKING } from 'constant/analytics';
import PoweredByMomos from 'components/PoweredByMomos';
import { Brand, MicrositeConfig } from 'utils/types';
import { GetServerSideProps } from 'next';

const { TabPane } = Tabs;

let enableInteraction = true;

interface MenusProps {
  slug: string;
  data: Record<string, any>;
  brandDetails: Brand;
  micrositeConfig: MicrositeConfig;
}

export default function Menus({ slug, data, brandDetails, micrositeConfig }: MenusProps) {
  const sectionRefs: any = useRef<HTMLDivElement[]>([]);
  const [selectedTab, setSelectedTab] = useState('0');

  const [selectedMenu, setSelectedMenu] = useState<any>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const sortCategories: any = data?.categories?.sort((a: any, b: any) => {
    return a.sequence - b.sequence;
  });

  const sortCategoryItems = (items = []) => {
    return items.sort((a: any, b: any) => {
      return a.sortOrder - b.sortOrder;
    });
  };

  const scrollToNode = (index: string) => {
    setSelectedTab(`${index}`);
    enableInteraction = false;
    setTimeout(() => {
      enableInteraction = true;
    }, 700);
    const elementToScroll = sectionRefs?.current?.[index];
    const position = elementToScroll.getBoundingClientRect();
    if (isClient()) {
      window.scrollTo({ left: position.left, top: position.top + window.scrollY - 70, behavior: 'smooth' });
    }
  };

  const onClickMenuItem = (item: React.MouseEvent<HTMLDivElement>) => {
    ga.event(TRACKING.MENU.MENU_ITEM.ITEM);
    setSelectedMenu(item);
    showModal();
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const onClickReservationButton = () => {
    ga.event(TRACKING.MENU.BUTTON.MAKE_A_RESERVATION);
    router.push(`/${slug}/make-reservation`);
  };

  const onClickDeliveryButton = () => {
    ga.event(TRACKING.MENU.BUTTON.ORDER_FOR_DELIVERY);
    router.push(`/${slug}/order-for-delivery`);
  };

  return (
    <PageWrap>
      <MetaHeader
        title={data.title}
        image={brandDetails?.logoUrl ? brandDetails?.logoUrl : undefined}
        pixelId={micrositeConfig?.micrositePixelId}
      />
      <Header brandDetails={brandDetails} hideMenuLink fixedHeader={false} theme={micrositeConfig?.micrositeTheme} />
      <Container>
        <PageTitle>
          <h1>{data.title}</h1>
        </PageTitle>
        <div className="scrollable-sections">
          <Tabs
            activeKey={selectedTab}
            onChange={scrollToNode}
            className="category-select-desk"
            tabBarExtraContent={
              (micrositeConfig?.reservationFlag || micrositeConfig?.foodDeliveryFlag) && {
                right: (
                  <TabsActionWrap>
                    {micrositeConfig?.reservationFlag && (
                      <IconButton className="tab-action-button" icon={<NoteIcon />} onClick={onClickReservationButton}>
                        Make a Reservation
                      </IconButton>
                    )}
                    {micrositeConfig?.foodDeliveryFlag && (
                      <IconButton
                        className="tab-action-button"
                        icon={<TruckSmallIcon />}
                        onClick={onClickDeliveryButton}
                      >
                        Order
                      </IconButton>
                    )}
                  </TabsActionWrap>
                ),
              }
            }
          >
            {sortCategories?.map((category: any, index: string) => (
              <TabPane tab={category.name} id={index} key={index} />
            ))}
          </Tabs>
          <div className="sections-wrap">
            {sortCategories?.map((category: any, index: string) => (
              <InView
                as="section"
                onChange={(inView) => {
                  if (inView && enableInteraction) {
                    setSelectedTab(`${index}`);
                  }
                }}
                className="menu-list-wrap"
                key={index}
                rootMargin="-50% 0px"
              >
                <div ref={(el) => (sectionRefs.current[index] = el)}>
                  <h2>{category.name}</h2>
                  <Row gutter={[16, 24]}>
                    {category.items &&
                      category.items.length > 0 &&
                      sortCategoryItems(category.items).map((menu: any, subkey) => (
                        <Col className="gutter-row" xs={12} sm={12} lg={8} xxl={6} key={subkey}>
                          <MenuItem
                            title={menu.name}
                            description={menu.description}
                            price={menu.price}
                            image={menu?.photos?.default?.[0]?.imageUrl}
                            onClick={() => onClickMenuItem(menu)}
                            currency={menu.currency}
                          />
                        </Col>
                      ))}
                  </Row>
                </div>
              </InView>
            ))}
            {isModalVisible && (
              <Modal
                title=""
                visible={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                closable={false}
                bodyStyle={{ padding: '0px' }}
                width={350}
              >
                <MenuDetails
                  title={selectedMenu && selectedMenu.title}
                  price={selectedMenu && selectedMenu.price}
                  description={selectedMenu && selectedMenu.description}
                  currency={selectedMenu && selectedMenu.currency}
                  image={selectedMenu && selectedMenu.photos?.default?.[0]?.imageUrl}
                  onCancel={handleCancel}
                />
              </Modal>
            )}
          </div>
        </div>
        <PoweredByMomos className="powered-custom-style" />
      </Container>

      {(micrositeConfig?.reservationFlag || micrositeConfig?.foodDeliveryFlag) && (
        <StickyFooter>
          {micrositeConfig?.reservationFlag && (
            <IconButton className="footer-button" icon={<NoteIcon />} onClick={onClickReservationButton}>
              Make a Reservation
            </IconButton>
          )}
          {micrositeConfig?.foodDeliveryFlag && (
            <IconButton className="footer-button" icon={<TruckSmallIcon />} onClick={onClickDeliveryButton}>
              Order
            </IconButton>
          )}
        </StickyFooter>
      )}
    </PageWrap>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ params, req }) => {
  const data = await getMicrositeMenus(req, params?.slug);

  const { menu, micrositeConfig = null, brandDetails = null } = data;

  return { props: { data: menu || [], slug: params?.slug, micrositeConfig, brandDetails } };
};

const PageWrap = styled.div`
  margin-bottom: 50px;

  @media (min-width: ${breakpoints.tablet}) {
    margin-bottom: 0px;
  }
`;

const PageTitle = styled.div`
  padding: 24px;

  h1 {
    font-size: 36px;
    line-height: 40px;
    text-transform: capitalize;
    font-family: var(--font-family-bold);
    margin-bottom: 0px;
  }
`;

const Container = styled.div`
  .menu-list-wrap {
    margin-bottom: 20px;

    h2 {
      margin-bottom: 30px;
    }
  }

  .scrollable-sections {
    position: relative;
  }

  .sections-wrap {
    padding-top: 30px;
    padding-bottom: 15px;
    padding-left: 20px;
    padding-right: 20px;
  }

  .category-select-desk {
    display: block;

    position: sticky;
    z-index: 1;
    background: #fff;
    top: 0px;
    transition: unset;

    .ant-tabs-tab {
      font-weight: bold;
      font-size: 16px;
      color: var(--grey-dark);
      padding: 12px 0px 12px 0px;
      margin: 0px;
      margin-right: 24px;
    }

    .ant-tabs-extra-content {
      .tab-action-button {
        margin: 0px;

        &:first-child {
          margin-right: 20px;
        }
      }
    }

    .ant-tabs-nav {
      padding: 0px 24px;
    }

    .ant-tabs-tab-btn:active,
    .ant-tabs-tab-btn:focus {
      color: var(--grey-dark) !important;
    }

    .ant-tabs-tab ant-tabs-tab-active {
      border-bottom-color: var(--orange);
    }

    .ant-tabs-ink-bar {
      background: var(--orange);
      height: 4px;
    }

    .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
      color: var(--grey-dark);
    }
  }

  .category-select-res {
    display: block;
    margin: 30px 0px;
    @media (min-width: ${breakpoints.tablet}) {
      display: none;
    }
  }

  .powered-custom-style {
    padding-bottom: 80px;
  }
`;

const TabsActionWrap = styled.div`
  flex-direction: row;
  align-items: center;
  display: flex;

  display: none;

  @media (min-width: ${breakpoints.tablet}) {
    display: flex;
  }

  .tab-action-button {
    margin-right: 20px;
  }
`;

const StickyFooter = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;

  @media (min-width: ${breakpoints.tablet}) {
    display: none;
  }

  .footer-button {
    flex: 1;
  }
`;
